import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { toast } from "sonner";
import { initializeAnalytics } from "./analytics.ts";
import App from "./App.tsx";
import { AuthProvider } from "./auth/AuthProvider.tsx";
import { config } from "./config";
import "./index.css";
import { AppProvider } from "./store/AppProvider.tsx";
import "./debug"; // Load debug utilities

// Detailed logging for deployment debugging
console.log("🚀 App initialization started");
console.log("📦 Environment:", import.meta.env.MODE);
console.log("🔧 Config loaded:", {
  apiUrl: config.apiUrl,
  appUrl: config.appUrl,
  sentryDsn: config.sentry.dsn ? "✅ Configured" : "❌ Missing",
  frigadeApiKey: config.frigade.apiKey ? "✅ Configured" : "❌ Missing",
  mixpanelToken: config.mixpanel.token ? "✅ Configured" : "❌ Missing"
});

if (config.sentry.dsn) {
  console.log("🔍 Initializing Sentry...");
  Sentry.init({
    dsn: config.sentry.dsn,
    environment: config.environment,
    // userInfo collects default identity data such as IP addresses.
    dataCollection: {
      userInfo: config.sentry.sendDefaultPii,
    },
  });
  console.log("✅ Sentry initialized");
} else {
  console.warn("⚠️ Sentry DSN not configured, error tracking disabled");
}

console.log("📊 Initializing analytics...");
try {
  initializeAnalytics();
  console.log("✅ Analytics initialized");
} catch (error) {
  console.error("❌ Analytics initialization failed:", error);
}

window.addEventListener("error", (event) => {
  console.error("🚨 Global error caught:", event.error);
  console.error("🔍 Error details:", {
    message: event.error?.message,
    stack: event.error?.stack,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
  Sentry.captureException(event.error);
  toast.error("Oops! Something went wrong.");
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("🚨 Unhandled promise rejection:", event.reason);
  console.error("🔍 Rejection details:", {
    reason: event.reason,
    promise: event.promise
  });
  Sentry.captureException(event.reason);
  toast.error("Oops! Something went wrong.");
});

console.log("🎯 Creating React root...");
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const router = createBrowserRouter([
  {
    path: "*",
    element: <App />,
  },
]);

try {
  createRoot(rootElement).render(
    <StrictMode>
      <AuthProvider>
        <AppProvider>
          <RouterProvider router={router} />
        </AppProvider>
      </AuthProvider>
    </StrictMode>
  );
  console.log("✅ React app rendered successfully");
} catch (error) {
  console.error("❌ Failed to render React app:", error);
    console.error("🔍 Render error details:", {
      message: error instanceof Error ? error.message : "Unknown error",
      stack: error instanceof Error ? error.stack : undefined,
      config: {
        apiUrl: config.apiUrl,
        appUrl: config.appUrl,
      }
    });
  throw error;
}
